<template>
  <div class="text-xs text-white/70 text-center" @click="present">Create Account</div>
  <IonModal ref="modal">
    <IonHeader>
      <IonToolbar color="white">
        <IonTitle color="medium">Registration</IonTitle>
        <IonButtons slot="end">
          <IconButton @click="dismiss" icon="icon-x"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <div class="py-4">
        <div class="text-gray-500 font-medium mb-4 px-4">We'll send an email for account verification</div>
        <IonList inset>
          <StackedTextInput
            title="Email"
            v-model="email.value"
            type="email"
            :errors="email.errors"
            :state="email.state"
            gray
          ></StackedTextInput>
          <StackedTextInput
            title="First Name"
            v-model="firstName.value"
            :errors="firstName.errors"
            :state="firstName.state"
            gray
          ></StackedTextInput>
          <StackedTextInput
            title="Last Name"
            v-model="lastName.value"
            :errors="lastName.errors"
            :state="lastName.state"
            gray
          ></StackedTextInput>
        </IonList>
        <PasswordHelperGraphic
          class="my-6"
          :new-password="password"
          :confirm-password="confirmPassword"
          :includeCurrentPassword="false"
        ></PasswordHelperGraphic>
        <IonList inset>
          <StackedTextInput
            title="New Password"
            v-model="password.value"
            type="password"
            :errors="password.errors"
            :state="password.state"
            gray
          ></StackedTextInput>
          <StackedTextInput
            title="Confirm Password"
            v-model="confirmPassword.value"
            type="password"
            :errors="confirmPassword.errors"
            :state="confirmPassword.state"
            gray
          ></StackedTextInput>
        </IonList>
      </div>
    </IonContent>
    <IonFooter class="ion-no-border">
      <hr class="mx-3" />
      <IonToolbar color="white">
        <PrimaryBlockButton
          class="mt-6 mx-1"
          :disabled="!canSubmit"
          type="button"
          data-test-id="createAccount"
          @click="submit"
          >Create Account</PrimaryBlockButton
        >
        <LightBlockButton class="mt-3 mx-1" type="button" @click="dismiss">Cancel</LightBlockButton>
      </IonToolbar>
    </IonFooter>
  </IonModal>
</template>

<script setup lang="ts">
import { useFormControl } from '@/composables/use-form-control';
import { IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar, IonList, IonFooter } from '@ionic/vue';
import { computed, ref, watch } from 'vue';
import PasswordHelperGraphic from './PasswordHelperGraphic.vue';
import IconButton from '../forms/IconButton.vue';
import PrimaryBlockButton from '../forms/PrimaryBlockButton.vue';
import LightBlockButton from '../forms/LightBlockButton.vue';
import StackedTextInput from '../forms/stacked/StackedTextInput.vue';
import { EmailValidator, PasswordValidator } from '@/composables/validators/account-validators';
import { EqualsValidator } from '@/composables/validators/general-validators';
import { RequiredFieldValidator } from '@/composables/validators/required';
import { UserService } from '@/http-services/user.service';
import { useToast } from '@/composables/use-toast';
import { UsernameOrEmailValidator } from '@/composables/validators/usernameoremail';
import { EmojiValidator } from '@/composables/validators/emoji-validator';
import { LettersOnlyValidator } from '@/composables/validators/letters-only-validator';
import { MaxLengthValidator } from '@/composables/validators/max-length';

const userService = new UserService();
const modal = ref<InstanceType<typeof IonModal>>();
const isSaving = ref(false);
const toast = useToast();
const email = useFormControl(() => '', {
  validators: [EmailValidator(), EmojiValidator(), MaxLengthValidator(50)],
  asyncValidators: [UsernameOrEmailValidator(userService)],
});
const firstName = useFormControl(() => '', {
  validators: [RequiredFieldValidator(), EmojiValidator(), LettersOnlyValidator(), MaxLengthValidator(50)],
});
const lastName = useFormControl(() => '', {
  validators: [RequiredFieldValidator(), EmojiValidator(), LettersOnlyValidator(), MaxLengthValidator(50)],
});
const password = useFormControl(() => '', {
  validators: [RequiredFieldValidator(), PasswordValidator()],
});
const confirmPassword = useFormControl(() => undefined, {
  validators: [RequiredFieldValidator(), EqualsValidator(() => password.value, 'Passwords must be the same')],
});

const canSubmit = computed((): boolean => {
  return (
    email.value &&
    email.valid &&
    firstName.value &&
    firstName.valid &&
    lastName.value &&
    lastName.valid &&
    password.value &&
    password.valid &&
    confirmPassword.value &&
    confirmPassword.valid &&
    !isSaving.value
  );
});

watch(
  () => password.value,
  () => {
    confirmPassword.updateValidity();
  }
);

function present() {
  email.reset();
  firstName.reset();
  lastName.reset();
  password.reset();
  confirmPassword.reset();
  modal.value?.$el.present();
  isSaving.value = false;
}

function dismiss() {
  modal.value?.$el.dismiss();
}

async function submit() {
  isSaving.value = true;
  try {
    const res: any = await userService.createAccount({
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
      password: password.value,
    });
    if (res.success) {
      dismiss();
      toast.successToast(res.message, { duration: 4000 });
    } else {
      toast.errorToast(res.message, { duration: 3000 });
    }
  } catch {
    toast.errorToast('Error creating account', { duration: 3000 });
  } finally {
    isSaving.value = false;
  }
}
</script>
